import { createReducer, on } from '@ngrx/store';
import { DataStore } from '@src/suite/common/data-models/data-store/data-store';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { FilterDef } from '@src/suite/common/data-models/navigation/filter-def';
import { MultipleFeedViewStateRecord } from '@src/suite/common/data-models/records/mutiple-feed-viewstate-record';
import { PublicationViewStateRecord } from '@src/suite/common/data-models/records/publication-viewstate-record';
import { RecordFactory } from '@src/suite/common/data-models/records/record-factory';
import { RSSFeedViewStateRecord } from '@src/suite/common/data-models/records/rss-feed-viewstate-record';
import { SingleFeedViewStateRecord } from '@src/suite/common/data-models/records/single-feed-viewstate-record';
import { SingleViewStateRecord } from '@src/suite/common/data-models/records/single-viewstate-record';
import { TopicViewStateRecord } from '@src/suite/common/data-models/records/topic-viewstate-record';
import { TwitterFeedViewStateRecord } from '@src/suite/common/data-models/records/twitter-feed-viewstate-record';
import { ViewStateRecord } from '@src/suite/common/data-models/records/viewstate-record';
import { ViewStateSequenceSerializer } from '@src/suite/common/data-models/serializers/serializers';
import { ViewStateSortOders } from '@src/suite/common/enums/viewstate-sort-orders';
import { MultipleFeedViewStateMode, ViewStateTypes, ViewStateTypesEnum } from '@src/suite/common/enums/viewstate-types';

import { CreateViewStateActions } from './createviewstate.actions';
import { CreateViewStateState } from './createviewstate.models';

export const initialState: CreateViewStateState = {
    isLoading: false,
    isCopy: false,

    currentTitle: '',
    viewState: null,
    inEditMode: false,
    selectedGroup: null,
    selectedSection: null,
    selectedItem: null,

    inAddContentSourceMode: false,
    inBrowseMode: false,
    inConfigureMode: false,
    multipleFeedViewState: null,
    multipleFeedViewStateMode: MultipleFeedViewStateMode.NONE,
    multipleFeedViewStateSequence: [],
};

function handleUpdateViewState(state: CreateViewStateState, vs: ViewStateRecord): CreateViewStateState {
    const newViewState = RecordFactory.createViewStateForType(vs.viewStateType);
    vs.copyDataTo(newViewState, true);
    return {
        ...state,
        viewState: newViewState,
    };
}

export const createViewStateReducer = createReducer(
    initialState,
    on(CreateViewStateActions.initializeViewState, (state, _): CreateViewStateState => {
        return {
            ...state,
            inAddContentSourceMode: false,
            inBrowseMode: false,
            inConfigureMode: false,
            multipleFeedViewState: null,
            multipleFeedViewStateMode: MultipleFeedViewStateMode.NONE,
            viewState: null,
            isLoading: true,
        };
    }),
    on(CreateViewStateActions.initializeViewStateSuccess, (state, action): CreateViewStateState => {
        const vstype = action.vs ? action.vs.viewStateType : action.vstype;
        const vs = RecordFactory.createViewStateForType(vstype);

        if (action.title) vs.title = action.title;

        if (action.copy && action.vs) {
            action.vs.copyDataTo(vs, false);
            vs.title = vs.title + ' (Copy)';
        }

        if (action.edit) {
            action.vs.copyDataTo(vs, true);
        }

        if ((vs instanceof SingleViewStateRecord || vs instanceof PublicationViewStateRecord) && action.layout) {
            const layout = action.layout.deepCopy();
            layout.Value = vs.viewStateLayout.Value || layout.Default;
            vs.viewStateLayout = layout;
        }

        if (vs instanceof SingleViewStateRecord && action.groupby) {
            const groupby = action.groupby.deepCopy();
            groupby.Value = vs.viewStateGrouping.Value || groupby.Default;
            vs.viewStateGrouping = groupby;
        }

        if (vs instanceof SingleViewStateRecord && action.sortby) {
            const sortby = action.sortby.deepCopy();
            sortby.Value = vs.viewStateSorting.Value || sortby.Default;
            vs.viewStateSorting = sortby;
        }

        if (vs instanceof MultipleFeedViewStateRecord) {
            const viewstates = DataStore.getSingleViewStates('discover');
            vs.viewStateSequence = ViewStateSequenceSerializer.Instance.initialize(viewstates, vs.viewStateSequence);
        }

        return {
            ...state,
            currentTitle: vs.title,
            viewState: vs,
            isLoading: false,
            isCopy: action.copy,
            inEditMode: action.edit,
            selectedGroup: action.group,
            selectedSection: action.section,
            selectedItem: action.item,
        };
    }),
    on(CreateViewStateActions.initializeViewStateError, (state, _): CreateViewStateState => {
        return {
            ...state,
            isLoading: false,
        };
    }),
    on(CreateViewStateActions.enterBrowseModeSuccess, (state, _): CreateViewStateState => {
        if (state.viewState instanceof MultipleFeedViewStateRecord || (state.multipleFeedViewState && state.multipleFeedViewState instanceof MultipleFeedViewStateRecord)) {
            const viewstates = DataStore.getSingleViewStates('discover');

            const vs = RecordFactory.createViewStateForType<MultipleFeedViewStateRecord>(ViewStateTypes.MULTIPLEFEED);

            if (state.multipleFeedViewState) {
                state.multipleFeedViewState.copyDataTo(vs, true);
            } else {
                state.viewState.copyDataTo(vs, true);
            }

            vs.viewStateSequence = ViewStateSequenceSerializer.Instance.initialize(viewstates, vs.viewStateSequence);

            return {
                ...state,
                viewState: vs,
                multipleFeedViewState: null,
                multipleFeedViewStateMode: MultipleFeedViewStateMode.BROWSE,
                inBrowseMode: true,
                inConfigureMode: false,
                inAddContentSourceMode: true,
                isLoading: false,
            };
        }

        return {
            ...state,
            isLoading: false,
        };
    }),
    on(CreateViewStateActions.enterBrowseMode, (state, _): CreateViewStateState => {
        if (state.viewState && state.viewState instanceof MultipleFeedViewStateRecord) {
            const vs = state.viewState as MultipleFeedViewStateRecord;
            const selected = vs.viewStateSequence.viewstates.filter(item => item.Selected).map(item => item.Guid);

            return {
                ...state,
                multipleFeedViewStateSequence: selected,
                isLoading: true,
            };
        }

        return {
            ...state,
            isLoading: true,
        };
    }),
    on(CreateViewStateActions.enterConfigureMode, (state, _): CreateViewStateState => {
        const vs = RecordFactory.createViewStateForType(ViewStateTypes.RSSFEED);
        const multifeed = <MultipleFeedViewStateRecord>state.viewState;

        return {
            ...state,
            viewState: vs,
            multipleFeedViewState: multifeed,
            multipleFeedViewStateMode: MultipleFeedViewStateMode.CONFIGURE,
            inBrowseMode: false,
            inConfigureMode: true,
            inAddContentSourceMode: true,
            isLoading: false,
        };
    }),
    on(CreateViewStateActions.cancelAddViewStates, (state, _): CreateViewStateState => {
        if (state.inConfigureMode) {
            const multifeed = <MultipleFeedViewStateRecord>state.multipleFeedViewState;
            return {
                ...state,
                viewState: multifeed,
                multipleFeedViewState: null,
                multipleFeedViewStateMode: MultipleFeedViewStateMode.NONE,
                inBrowseMode: false,
                inConfigureMode: false,
                inAddContentSourceMode: false,
                isLoading: false,
            };
        }
        if (state.inBrowseMode) {
            const newViewState = RecordFactory.createViewStateForType<MultipleFeedViewStateRecord>(ViewStateTypes.MULTIPLEFEED);

            const vs = <MultipleFeedViewStateRecord>state.viewState;
            const sequence = vs.viewStateSequence.deepCopy();
            sequence.viewstates.forEach(item => {
                if (state.multipleFeedViewStateSequence.find(guid => guid === item.Guid)) {
                    item.Selected = true;
                } else {
                    item.Selected = false;
                }
            });

            vs.copyDataTo(newViewState, true);
            newViewState.viewStateSequence = sequence;

            return {
                ...state,
                viewState: newViewState,
                multipleFeedViewStateMode: MultipleFeedViewStateMode.NONE,
                inBrowseMode: false,
                inConfigureMode: false,
                inAddContentSourceMode: false,
                isLoading: false,
            };
        }
        return state;
    }),
    on(CreateViewStateActions.saveAddViewStatesSuccess, (state, _): CreateViewStateState => {
        if (state.inConfigureMode && state.multipleFeedViewState) {
            const multifeed = <MultipleFeedViewStateRecord>state.multipleFeedViewState;
            return {
                ...state,
                viewState: multifeed,
                multipleFeedViewState: null,
                multipleFeedViewStateMode: MultipleFeedViewStateMode.NONE,
                inBrowseMode: false,
                inConfigureMode: false,
                inAddContentSourceMode: false,
                isLoading: false,
            };
        }
        return {
            ...state,
            multipleFeedViewStateMode: MultipleFeedViewStateMode.NONE,
            inBrowseMode: false,
            inConfigureMode: false,
            inAddContentSourceMode: false,
            isLoading: false,
        };
    }),
    on(CreateViewStateActions.updateViewStateTitle, (state, action) => {
        if (state.viewState) {
            const vs = RecordFactory.createViewStateForType(state.viewState.viewStateType);
            state.viewState.copyDataTo(vs, true);
            vs.title = action.title;
            return {
                ...state,
                currentTitle: action.title,
                viewState: vs,
            };
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateLayout, (state, action) => {
        if (state.viewState) {
            const vs = state.viewState as SingleViewStateRecord;
            const label = vs.viewStateLayout.Options.filter(item => item.id === action.layout)[0]?.title;
            vs.viewStateLayout.Value = action.layout;
            vs.viewStateLayout.Label = label;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateGroupBy, (state, action) => {
        if (state.viewState) {
            const vs = state.viewState as SingleViewStateRecord;
            vs.viewStateGrouping.Value = action.groupBy;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateSortBy, (state, action) => {
        if (state.viewState) {
            const vs = state.viewState as SingleViewStateRecord;
            vs.viewStateSorting.Value = action.sortBy;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateGroupBySortOrder, state => {
        if (state.viewState) {
            const vs = state.viewState as SingleViewStateRecord;
            vs.viewStateGroupSortOrder = vs.viewStateGroupSortOrder === ViewStateSortOders.ASC ? ViewStateSortOders.DESC : ViewStateSortOders.ASC;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateSortBySortOrder, state => {
        if (state.viewState && state.viewState instanceof SingleViewStateRecord) {
            const vs = state.viewState as SingleViewStateRecord;
            vs.viewStateSortOrder = vs.viewStateSortOrder === ViewStateSortOders.ASC ? ViewStateSortOders.DESC : ViewStateSortOders.ASC;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.toggleGroupSortOder, state => {
        if (state.viewState && state.viewState instanceof SingleViewStateRecord) {
            const vs = state.viewState as SingleViewStateRecord;
            vs.viewStateGroupSortOrder = vs.viewStateGroupSortOrder === ViewStateSortOders.ASC ? ViewStateSortOders.DESC : ViewStateSortOders.ASC;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.toggleSortOder, state => {
        if (state.viewState && state.viewState instanceof SingleViewStateRecord) {
            const vs = state.viewState as SingleViewStateRecord;
            vs.viewStateSortOrder = vs.viewStateSortOrder === ViewStateSortOders.ASC ? ViewStateSortOders.DESC : ViewStateSortOders.ASC;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateLockMapLocation, (state, action) => {
        if (state.viewState && state.viewState instanceof TopicViewStateRecord) {
            const vs = state.viewState as TopicViewStateRecord;
            vs.viewStateMapLockMapLocation = action.locked;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateMapShowTeamLocations, (state, action) => {
        if (state.viewState && state.viewState instanceof TopicViewStateRecord) {
            const vs = state.viewState as TopicViewStateRecord;
            vs.viewStateMapShowTeamLocations = action.show;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateMapZoomToCoverage, (state, action) => {
        if (state.viewState && state.viewState instanceof TopicViewStateRecord) {
            const vs = state.viewState as TopicViewStateRecord;
            vs.viewStateMapZoomToCoverage = action.zoom;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.addViewStateFilters, (state, action): CreateViewStateState => {
        if (state.viewState instanceof SingleViewStateRecord) {
            const vs = <SingleViewStateRecord>RecordFactory.createViewStateForType(state.viewState.viewStateType);
            state.viewState.copyDataTo(vs, true);

            const filter = state.viewState.viewStateFilter.deepCopy();
            const filters: Array<FilterDef> = filter.allFilters.filter(item => {
                return !!action.filters.find(f => f === item.ID);
            });
            if (action.required) {
                filter.requiredFilters = filters;
                filter.onFilterStateChange(true);
            } else {
                filter.optionalFilters = filters;
                filter.onFilterStateChange(false);
            }
            vs.viewStateFilter = filter;

            return {
                ...state,
                viewState: vs,
            };
        }
    }),
    on(CreateViewStateActions.removeViewStateFilter, (state, action): CreateViewStateState => {
        if (state.viewState instanceof SingleViewStateRecord) {
            const vs = <SingleViewStateRecord>RecordFactory.createViewStateForType(state.viewState.viewStateType);
            state.viewState.copyDataTo(vs, true);

            const filter = state.viewState.viewStateFilter.deepCopy();
            if (action.required) {
                filter.requiredFilters = filter.requiredFilters.filter(f => f.ID !== action.filter);
                filter.onFilterStateChange(true);
            } else {
                filter.optionalFilters = filter.optionalFilters.filter(f => f.ID !== action.filter);
                filter.onFilterStateChange(false);
            }
            vs.viewStateFilter = filter;

            return {
                ...state,
                viewState: vs,
            };
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateFilter, state => {
        if (state.viewState) {
            const vs = state.viewState as SingleViewStateRecord;
            const filter = vs.viewStateFilter.deepCopy();
            vs.viewStateFilter = filter;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.removeAllViewStateFilters, (state, action): CreateViewStateState => {
        if (state.viewState instanceof SingleViewStateRecord) {
            const vs = <SingleViewStateRecord>RecordFactory.createViewStateForType(state.viewState.viewStateType);
            state.viewState.copyDataTo(vs, true);

            const filter = state.viewState.viewStateFilter.deepCopy();
            if (action.required) {
                filter.requiredFilters = [];
                filter.onFilterStateChange(true);
            } else {
                filter.optionalFilters = [];
                filter.onFilterStateChange(false);
            }
            vs.viewStateFilter = filter;

            return {
                ...state,
                viewState: vs,
            };
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateShowTopics, (state, action) => {
        if (state.viewState && state.viewState instanceof PublicationViewStateRecord) {
            const vs = state.viewState as PublicationViewStateRecord;
            vs.viewStateShowTopics = action.show;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateShowAssignments, (state, action) => {
        if (state.viewState && state.viewState instanceof PublicationViewStateRecord) {
            const vs = state.viewState as PublicationViewStateRecord;
            vs.viewStateShowAssignments = action.show;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateShowStories, (state, action) => {
        if (state.viewState && state.viewState instanceof PublicationViewStateRecord) {
            const vs = state.viewState as PublicationViewStateRecord;
            vs.viewStateShowStories = action.show;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateRemoveAddedRecords, (state, action) => {
        if (state.viewState && state.viewState instanceof PublicationViewStateRecord) {
            const vs = state.viewState as PublicationViewStateRecord;
            vs.viewStateRemoveAddedRecords = action.remove;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateGuid, (state, action) => {
        if (state.viewState && state.viewState instanceof PublicationViewStateRecord) {
            const vs = state.viewState as PublicationViewStateRecord;
            vs.viewStateGUID = action.guid;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStatePublicationChannelGuid, (state, action) => {
        if (state.viewState && state.viewState instanceof PublicationViewStateRecord) {
            const vs = state.viewState as PublicationViewStateRecord;
            vs.publicationChannelGUID = action.guid;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateEditionName, (state, action) => {
        if (state.viewState && state.viewState instanceof PublicationViewStateRecord) {
            const vs = state.viewState as PublicationViewStateRecord;
            vs.editionName = action.name;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStatePublicationListTitle, (state, action) => {
        if (state.viewState && state.viewState instanceof PublicationViewStateRecord) {
            const vs = state.viewState as PublicationViewStateRecord;
            vs.publicationListTitle = action.title;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStatePublicationTime, (state, action) => {
        if (state.viewState && state.viewState instanceof PublicationViewStateRecord) {
            const vs = state.viewState as PublicationViewStateRecord;

            if (vs.viewStateFilter && vs.viewStateFilter.allFilters) {
                const filter = state.viewState.viewStateFilter.deepCopy();
                const publicationTime = filter.allFilters.find(x => x.ID === PSTFieldNames.PSTPublicationTime);
                if (publicationTime) {
                    publicationTime.NotUsed = false;
                    publicationTime.Value = action.time;
                    vs.viewStateFilter = filter;
                    return handleUpdateViewState(state, vs);
                }
            }
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateFeedUrl, (state, action) => {
        if (state.viewState && state.viewState instanceof RSSFeedViewStateRecord) {
            const vs = state.viewState as RSSFeedViewStateRecord;
            vs.viewStateFeedURL = action.url;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateFeedId, (state, action) => {
        if (state.viewState && state.viewState instanceof SingleFeedViewStateRecord) {
            const vs = state.viewState as SingleFeedViewStateRecord;
            vs.viewStateFeedID = action.id;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateTwitterUsernames, (state, action) => {
        if (state.viewState && state.viewState instanceof TwitterFeedViewStateRecord) {
            const vs = state.viewState as TwitterFeedViewStateRecord;
            vs.viewStateFeedNames = JSON.stringify(action.usernames);
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateSequence, (state, action) => {
        if (state.viewState && state.viewState instanceof MultipleFeedViewStateRecord) {
            const vs = state.viewState as MultipleFeedViewStateRecord;
            vs.viewStateSequence.viewstates.forEach(item => {
                if (action.selected.find(guid => guid === item.Guid)) {
                    item.Selected = true;
                }
                if (action.unselected.find(guid => guid === item.Guid)) {
                    item.Selected = false;
                }
            });
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.removeAllViewStates, (state, _) => {
        if (state.viewState && state.viewState instanceof MultipleFeedViewStateRecord) {
            const vs = state.viewState as MultipleFeedViewStateRecord;
            vs.viewStateSequence.viewstates.forEach(item => {
                item.Selected = false;
            });
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.removeViewState, (state, action) => {
        if (state.viewState && state.viewState instanceof MultipleFeedViewStateRecord) {
            const vs = state.viewState as MultipleFeedViewStateRecord;
            vs.viewStateSequence.viewstates.forEach(item => {
                if (action.guid === item.Guid) item.Selected = false;
            });
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.setStoredViewStates, (state, action) => {
        if (state.viewState && state.viewState instanceof PublicationViewStateRecord) {
            const vs = state.viewState;
            state.storedViewStates = action.viewStates.filter(x => x.viewStateType === ViewStateTypesEnum.TOPIC || x.viewStateType === ViewStateTypesEnum.ASSIGNMENT);
            return handleUpdateViewState(state, vs);
        }
        return state;
    })
);
